// WebAuthn wire-conversion helpers. The server (go-webauthn) and the browser
// exchange credential options and responses as JSON where every binary field
// is base64url (no padding); `navigator.credentials` instead wants/returns
// ArrayBuffers. These helpers bridge the two, keeping the ceremony JSON opaque
// to the protocol (frontend/protocol WebAuthnJson) — just like SDP is opaque
// server-side. Nothing here restores identity: passkeys sign the DEVICE into
// the server only (docs/03 §2).

import type { WebAuthnJson } from '@merkury/protocol';
import { fromBase64, randomBytes, toBase64 } from '@merkury/brain/lib/bytes.js';
import { PRF_RESTORE_SALT, derivePasskeyWrapKey, encryptIdentitySeed } from '@merkury/brain/identity/kdf.js';
// Global probe only — platform.js imports no Capacitor, so the web bundle stays
// clean; the actual @capacitor/core module is reached exclusively via the
// dynamic import in loadCapacitor() below, guarded by isCapacitorNative().
import { isCapacitorNative } from '../shell/capacitor/platform.js';

/**
 * True when this platform can run WebAuthn ceremonies. On the Capacitor native
 * app the WebView has no `navigator.credentials` WebAuthn (platform policy), but
 * the native MerkuryPasskeys plugin (androidx Credential Manager) speaks the
 * same WebAuthn JSON — so native counts as supported and the ceremony routes
 * through the plugin (see createCredential/getAssertion).
 */
export function supportsWebAuthn(): boolean {
  if (isCapacitorNative()) return true;
  return (
    typeof globalThis.navigator !== 'undefined' &&
    typeof globalThis.PublicKeyCredential !== 'undefined' &&
    !!globalThis.navigator.credentials?.create &&
    !!globalThis.navigator.credentials?.get
  );
}

// Cache the MODULE, never the plugin proxy: resolving a promise WITH a
// Capacitor plugin proxy makes the JS thenable-check access `.then` on it,
// which the proxy forwards as a native call that never resolves (field-debugged
// on device — see RingtoneSettingsScreen createRingtoneBridge). The proxy is
// only ever method-called, never awaited.
let capacitorModule: Promise<typeof import('../shell/capacitor/index.js')> | undefined;
function loadCapacitor(): NonNullable<typeof capacitorModule> {
  return (capacitorModule ??= import('../shell/capacitor/index.js'));
}

/** Unwrap go-webauthn's `{publicKey:{...}}` to the bare options object the
 *  native Credential Manager / navigator.credentials both take. */
function unwrapPublicKey(json: WebAuthnJson): unknown {
  return (json as { publicKey?: unknown }).publicKey ?? json;
}

/**
 * Run the registration ceremony against server-issued creation options and
 * return the credential as go-webauthn JSON (ready for POST
 * /auth/passkeys/register). Platform seam: native routes through the
 * MerkuryPasskeys plugin with stringified JSON in/out; web uses
 * `navigator.credentials.create`. Resolves `undefined` only when the ceremony
 * yields no credential; a user-cancel rejects (callers catch it).
 */
export async function createCredential(options: WebAuthnJson): Promise<WebAuthnJson | undefined> {
  if (isCapacitorNative()) {
    const native = await loadCapacitor();
    const optionsJson = JSON.stringify(unwrapPublicKey(options));
    const { credentialJson } = await native.nativePlugins().passkeys.createPasskey({ optionsJson });
    return JSON.parse(credentialJson) as WebAuthnJson;
  }
  // ACTIVATION-SAFETY (Safari): navigator.credentials.create must fire inside
  // the same transient user activation as the click — no `await` may sit
  // between this function's entry and the call below, or Safari drops the
  // gesture and rejects with NotAllowedError. The options are passed in already
  // fetched for exactly this reason. Guarded by the "no async gap" test.
  const publicKey = creationOptionsFromJson(options);
  const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  return credential ? attestationToJson(credential) : undefined;
}

/**
 * Run the assertion ceremony against server-issued request options and return
 * the credential as go-webauthn JSON (ready for POST /auth/passkeys/login).
 * Same platform seam as createCredential.
 */
export async function getAssertion(options: WebAuthnJson): Promise<WebAuthnJson | undefined> {
  if (isCapacitorNative()) {
    const native = await loadCapacitor();
    const optionsJson = JSON.stringify(unwrapPublicKey(options));
    const { credentialJson } = await native
      .nativePlugins()
      .passkeys.getPasskeyAssertion({ optionsJson });
    return JSON.parse(credentialJson) as WebAuthnJson;
  }
  // ACTIVATION-SAFETY (Safari): keep navigator.credentials.get in the same
  // transient activation as the click — no `await` before it (see createCredential).
  const publicKey = requestOptionsFromJson(options);
  const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  return assertion ? assertionToJson(assertion) : undefined;
}

// ---------------------------------------------------------------------------
// PRF-based cross-device identity restore (docs/03 §2).
//
// The WebAuthn PRF extension lets a *synced* passkey derive a stable
// per-credential secret (the "PRF output") that the SERVER NEVER SEES — it is
// returned only to the client, in the assertion's client-extension results. We
// HKDF that output into a wrapping key and encrypt the identity seed under it,
// so the server stores only ciphertext (same threat model as the password
// restore path, docs/03 §3a). Everything here feature-detects PRF and degrades
// to a session-only passkey rather than failing the ceremony.
// ---------------------------------------------------------------------------

/** One or two PRF evaluation salts for a ceremony. `first` is always the
 *  fixed app-wide restore salt going forward; `second` (when known) is the
 *  account's legacy per-user salt so old wrapped blobs stay restorable in the
 *  same single assertion. */
export interface PrfSalts {
  first: Uint8Array;
  second?: Uint8Array;
}

/** Result of a PRF-enabled ceremony: the credential JSON to POST to the server,
 *  plus the PRF output when the authenticator actually supported PRF. */
export interface PrfCeremony {
  credential: WebAuthnJson;
  /** 32-byte PRF output over the `first` salt; undefined ⇒ no PRF support. */
  prfOutput?: Uint8Array;
  /** PRF output over the `second` salt, when one was requested and returned. */
  prfOutputSecond?: Uint8Array;
  /** True when the authenticator acknowledged the PRF extension at creation. */
  prfEnabled: boolean;
}

function prfBytes(value: ArrayBuffer | ArrayBufferView | undefined): Uint8Array | undefined {
  if (!value) return undefined;
  return value instanceof ArrayBuffer
    ? new Uint8Array(value)
    : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
}

/** Read the PRF outputs from a web `PublicKeyCredential` (ArrayBuffers). */
function prfOutputsFromCredential(cred: PublicKeyCredential): {
  first?: Uint8Array;
  second?: Uint8Array;
} {
  const ext = cred.getClientExtensionResults() as {
    prf?: {
      results?: {
        first?: ArrayBuffer | ArrayBufferView;
        second?: ArrayBuffer | ArrayBufferView;
      };
    };
  };
  const first = prfBytes(ext.prf?.results?.first);
  const second = prfBytes(ext.prf?.results?.second);
  return { ...(first ? { first } : {}), ...(second ? { second } : {}) };
}

/** Read the PRF outputs from native credential JSON (Credential Manager encodes
 *  `clientExtensionResults.prf.results.*` as base64url strings). */
function prfOutputsFromJson(json: WebAuthnJson): { first?: Uint8Array; second?: Uint8Array } {
  const ext = (
    json as {
      clientExtensionResults?: { prf?: { results?: { first?: string; second?: string } } };
    }
  ).clientExtensionResults;
  const rawFirst = ext?.prf?.results?.first;
  const rawSecond = ext?.prf?.results?.second;
  const first =
    typeof rawFirst === 'string' && rawFirst.length > 0 ? base64urlToBytes(rawFirst) : undefined;
  const second =
    typeof rawSecond === 'string' && rawSecond.length > 0
      ? base64urlToBytes(rawSecond)
      : undefined;
  return { ...(first ? { first } : {}), ...(second ? { second } : {}) };
}

/** True when a credential JSON's client-extension results say PRF is enabled. */
function prfEnabledInJson(json: WebAuthnJson): boolean {
  const ext = (json as { clientExtensionResults?: { prf?: { enabled?: boolean; results?: unknown } } })
    .clientExtensionResults;
  return ext?.prf?.enabled === true || ext?.prf?.results !== undefined;
}

/** Merge a `prf.eval` extension (native JSON form: base64url salts). */
function withPrfExtensionJson(
  inner: Record<string, unknown>,
  salts: PrfSalts,
): Record<string, unknown> {
  const extensions = { ...((inner.extensions as Record<string, unknown>) ?? {}) };
  extensions.prf = {
    eval: {
      first: bytesToBase64url(salts.first),
      ...(salts.second ? { second: bytesToBase64url(salts.second) } : {}),
    },
  };
  return { ...inner, extensions };
}

/** The same `prf.eval` extension in the web (`BufferSource`) form. */
function prfEvalWeb(salts: PrfSalts): { eval: { first: BufferSource; second?: BufferSource } } {
  return {
    eval: {
      first: salts.first as BufferSource,
      ...(salts.second ? { second: salts.second as BufferSource } : {}),
    },
  };
}

/**
 * Registration with PRF: create the credential requesting the PRF extension.
 * Registration-time PRF results are unreliable across authenticators, so the
 * robust output comes from a follow-up assertion (see wrapSeedForPasskey); this
 * returns whatever the create step yielded plus the enabled flag.
 */
export async function createCredentialWithPrf(
  options: WebAuthnJson,
  salts: PrfSalts,
): Promise<PrfCeremony | undefined> {
  if (isCapacitorNative()) {
    const native = await loadCapacitor();
    const inner = withPrfExtensionJson(unwrapPublicKey(options) as Record<string, unknown>, salts);
    const { credentialJson } = await native
      .nativePlugins()
      .passkeys.createPasskey({ optionsJson: JSON.stringify(inner) });
    const credential = JSON.parse(credentialJson) as WebAuthnJson;
    const outputs = prfOutputsFromJson(credential);
    return {
      credential,
      prfEnabled: prfEnabledInJson(credential),
      ...(outputs.first ? { prfOutput: outputs.first } : {}),
      ...(outputs.second ? { prfOutputSecond: outputs.second } : {}),
    };
  }
  const publicKey = creationOptionsFromJson(options);
  publicKey.extensions = { ...(publicKey.extensions ?? {}), prf: prfEvalWeb(salts) };
  const cred = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;
  if (!cred) return undefined;
  const ext = cred.getClientExtensionResults() as { prf?: { enabled?: boolean } };
  const outputs = prfOutputsFromCredential(cred);
  return {
    credential: attestationToJson(cred),
    prfEnabled: ext.prf?.enabled === true || outputs.first !== undefined,
    ...(outputs.first ? { prfOutput: outputs.first } : {}),
    ...(outputs.second ? { prfOutputSecond: outputs.second } : {}),
  };
}

/**
 * Assertion with PRF over one or two salts. `options` is the (opaque) server
 * assertion options; the PRF extension is injected client-side. Returns the
 * assertion JSON (to POST for verification) plus the PRF output(s).
 */
export async function getAssertionWithPrf(
  options: WebAuthnJson,
  salts: PrfSalts,
): Promise<PrfCeremony | undefined> {
  if (isCapacitorNative()) {
    const native = await loadCapacitor();
    const inner = withPrfExtensionJson(unwrapPublicKey(options) as Record<string, unknown>, salts);
    const { credentialJson } = await native
      .nativePlugins()
      .passkeys.getPasskeyAssertion({ optionsJson: JSON.stringify(inner) });
    const credential = JSON.parse(credentialJson) as WebAuthnJson;
    const outputs = prfOutputsFromJson(credential);
    return {
      credential,
      prfEnabled: prfEnabledInJson(credential),
      ...(outputs.first ? { prfOutput: outputs.first } : {}),
      ...(outputs.second ? { prfOutputSecond: outputs.second } : {}),
    };
  }
  const publicKey = requestOptionsFromJson(options);
  publicKey.extensions = { ...(publicKey.extensions ?? {}), prf: prfEvalWeb(salts) };
  const assertion = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;
  if (!assertion) return undefined;
  const outputs = prfOutputsFromCredential(assertion);
  return {
    credential: assertionToJson(assertion),
    prfEnabled: outputs.first !== undefined,
    ...(outputs.first ? { prfOutput: outputs.first } : {}),
    ...(outputs.second ? { prfOutputSecond: outputs.second } : {}),
  };
}

/** Minimal assertion options targeting one just-created credential — used only
 *  to harvest the PRF output locally right after registration (never sent to
 *  the server). The challenge is irrelevant (no server verification).
 *
 *  `rpId` is NOT optional here even though the browser can infer it from the
 *  origin: on the native seams these options go verbatim into Credential
 *  Manager / ASAuthorization, which have no origin to infer from and reject
 *  the request outright. Omitting it made the harvest fail on every phone, so
 *  every passkey registered session-only and one-tap restore never armed. */
function localAssertionOptions(credentialIdB64url: string, rpId?: string): WebAuthnJson {
  return {
    publicKey: {
      challenge: bytesToBase64url(randomBytes(32)),
      allowCredentials: [{ id: credentialIdB64url, type: 'public-key' }],
      userVerification: 'required',
      timeout: 60_000,
      ...(rpId ? { rpId } : {}),
    },
  } as unknown as WebAuthnJson;
}

/**
 * The full registration wrap step (docs/03 §2): create the credential with PRF,
 * obtain the PRF output (via a follow-up assertion when the create step didn't
 * surface it — the reliable pattern), then HKDF→wrapKey and encrypt the seed.
 * The PRF is always evaluated over the FIXED app-wide restore salt
 * (`PRF_RESTORE_SALT`), so the blob is restorable by the one-tap discoverable
 * flow — no account id needed on the new device.
 * Returns the credential to register plus `encIdentityKey` when the passkey is
 * restore-capable; a missing `encIdentityKey` means a session-only passkey
 * (PRF unsupported) — the caller registers it anyway and tells the user it
 * won't restore a new device. Returns undefined only when create yielded no
 * credential (e.g. cancellation the caller surfaces).
 */
export async function wrapSeedForPasskey(
  options: WebAuthnJson,
  seed: Uint8Array,
): Promise<{ credential: WebAuthnJson; encIdentityKey?: Uint8Array; restorable: boolean } | undefined> {
  const salts: PrfSalts = { first: PRF_RESTORE_SALT };
  const created = await createCredentialWithPrf(options, salts);
  if (!created) return undefined;
  let prfOutput = created.prfOutput;
  if (!prfOutput && created.prfEnabled) {
    const credId =
      ((created.credential as { rawId?: string; id?: string }).rawId ??
        (created.credential as { id?: string }).id) ??
      '';
    if (credId) {
      // Carry the RP ID over from the registration options the server issued —
      // the native ceremonies cannot infer it.
      const asserted = await getAssertionWithPrf(
        localAssertionOptions(credId, rpIdFromOptions(options)),
        salts,
      ).catch(() => undefined);
      prfOutput = asserted?.prfOutput;
    }
  }
  if (!prfOutput) return { credential: created.credential, restorable: false };
  const wrapKey = derivePasskeyWrapKey(prfOutput);
  const encIdentityKey = encryptIdentitySeed(wrapKey, seed);
  return { credential: created.credential, encIdentityKey, restorable: true };
}

/**
 * The Relying Party ID the server pinned into these ceremony options. Creation
 * options carry it at `publicKey.rp.id`; request (assertion) options at
 * `publicKey.rpId`. Used only to name the primary domain in the ROR-degradation
 * message — the client never overrides it (the server owns the options).
 */
export function rpIdFromOptions(options: WebAuthnJson): string | undefined {
  const pk = ((options as { publicKey?: unknown }).publicKey ?? options) as Record<string, unknown>;
  const rp = pk.rp as { id?: unknown } | undefined;
  if (typeof rp?.id === 'string' && rp.id) return rp.id;
  if (typeof pk.rpId === 'string' && pk.rpId) return pk.rpId as string;
  return undefined;
}

/**
 * True when a ceremony failure is consistent with the browser lacking WebAuthn
 * Related Origin Requests support (Firefox today) while the page runs on a
 * MIRROR origin — i.e. the pinned `rpId` is not a registrable suffix of this
 * host. A ROR-less browser rejects such a ceremony with a `SecurityError`
 * (rpId not a suffix of the origin); some surface a bare `NotAllowedError`. When
 * this holds the caller should tell the user to switch to the primary domain.
 *
 * On the primary origin (host === rpId or a subdomain of it) this always returns
 * false, so an ordinary user cancellation is never mis-reported as a ROR problem.
 * `origin` defaults to the current page origin; injectable for tests.
 */
export function isRelatedOriginError(
  err: unknown,
  rpId: string | undefined,
  origin: string | undefined = globalThis.location?.origin,
): boolean {
  if (!rpId || !origin) return false;
  const name = (err as { name?: unknown } | null | undefined)?.name;
  if (name !== 'SecurityError' && name !== 'NotAllowedError') return false;
  let host: string;
  try {
    host = new URL(origin).hostname;
  } catch {
    return false;
  }
  // rpId is a valid RP ID here (primary domain or a subdomain of it): ROR is not
  // involved, so this error is something else (e.g. a real cancellation).
  if (host === rpId || host.endsWith('.' + rpId)) return false;
  return true;
}

/**
 * A ceremony failure, classified for user-facing copy. The three the UI must
 * distinguish (job card): the user dismissed the sheet (`cancelled`), the
 * platform can't run WebAuthn at all (`unsupported`), and the page is on a
 * mirror origin a ROR-less browser rejects (`wrongOrigin`). `unknown` is the
 * safe catch-all (surface the generic "try the phrase" message). Note: a
 * ceremony that RESOLVES with no credential (empty discoverable set, nothing
 * chosen) is not an error — the caller sees `undefined` and shows the
 * "no matching passkey" copy directly; only thrown DOMExceptions land here.
 */
export type WebAuthnFailure = 'cancelled' | 'unsupported' | 'wrongOrigin' | 'unknown';

/**
 * Map a thrown ceremony error to a `WebAuthnFailure`. `NotAllowedError` is
 * WebAuthn's overloaded "user dismissed / timed out" rejection — but on a
 * mirror origin a ROR-less browser raises the same name, so the ROR check runs
 * first (see isRelatedOriginError). `rpId`/`origin` are optional; without them
 * the ROR branch is skipped and a `NotAllowedError` reads as `cancelled`.
 */
export function classifyWebAuthnError(
  err: unknown,
  rpId?: string,
  origin: string | undefined = globalThis.location?.origin,
): WebAuthnFailure {
  if (isRelatedOriginError(err, rpId, origin)) return 'wrongOrigin';
  const name = (err as { name?: unknown } | null | undefined)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'AbortError':
      return 'cancelled';
    case 'NotSupportedError':
      return 'unsupported';
    default:
      return 'unknown';
  }
}

/**
 * True when this browser offers WebAuthn conditional mediation (autofill-style
 * passkey suggestions). Evaluated for the restore step, but it is an
 * ENHANCEMENT only: the explicit "Use passkey" button stays the universal path
 * (conditional mediation needs the assertion armed with no user gesture, which
 * the current options-fetch-then-ceremony seam can't provide without a
 * prefetch — see web/passkey-audit.md). Feature-detected and fail-closed so a
 * missing API never throws.
 */
export async function supportsConditionalMediation(): Promise<boolean> {
  try {
    const pkc = globalThis.PublicKeyCredential as
      | { isConditionalMediationAvailable?: () => Promise<boolean> }
      | undefined;
    return (await pkc?.isConditionalMediationAvailable?.()) === true;
  } catch {
    return false;
  }
}

export function bytesToBase64url(bytes: Uint8Array): string {
  return toBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64urlToBytes(s: string): Uint8Array {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/') + '=='.slice((s.length + 3) % 4);
  return fromBase64(padded);
}

function bufToB64url(buf: ArrayBuffer): string {
  return bytesToBase64url(new Uint8Array(buf));
}

interface CredentialDescriptorJson {
  id: string;
  type?: string;
  transports?: string[];
}

function descriptorsFromJson(list: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(list)) return undefined;
  return list.map((raw) => {
    const d = raw as CredentialDescriptorJson;
    return {
      id: base64urlToBytes(d.id) as BufferSource,
      type: 'public-key',
      ...(d.transports ? { transports: d.transports as AuthenticatorTransport[] } : {}),
    };
  });
}

/**
 * Convert a server creation-options object (`{publicKey: {...}}`, as returned
 * by `POST /auth/passkeys/register/options`) into
 * `PublicKeyCredentialCreationOptions` for `navigator.credentials.create`.
 */
export function creationOptionsFromJson(json: WebAuthnJson): PublicKeyCredentialCreationOptions {
  const pk = (json.publicKey ?? json) as Record<string, unknown>;
  const user = pk.user as { id: string; name: string; displayName: string };
  const exclude = descriptorsFromJson(pk.excludeCredentials);
  return {
    ...(pk as unknown as PublicKeyCredentialCreationOptions),
    challenge: base64urlToBytes(pk.challenge as string) as BufferSource,
    user: {
      ...user,
      id: base64urlToBytes(user.id) as BufferSource,
    },
    ...(exclude ? { excludeCredentials: exclude } : {}),
  };
}

/**
 * Convert a server request-options object (`{publicKey: {...}}`, as returned by
 * `POST /auth/passkeys/login/options`) into `PublicKeyCredentialRequestOptions`
 * for `navigator.credentials.get`.
 */
export function requestOptionsFromJson(json: WebAuthnJson): PublicKeyCredentialRequestOptions {
  const pk = (json.publicKey ?? json) as Record<string, unknown>;
  const allow = descriptorsFromJson(pk.allowCredentials);
  return {
    ...(pk as unknown as PublicKeyCredentialRequestOptions),
    challenge: base64urlToBytes(pk.challenge as string) as BufferSource,
    ...(allow ? { allowCredentials: allow } : {}),
  };
}

/** Serialize a registration `PublicKeyCredential` to the go-webauthn JSON shape. */
export function attestationToJson(cred: PublicKeyCredential): WebAuthnJson {
  const response = cred.response as AuthenticatorAttestationResponse;
  const transports =
    typeof response.getTransports === 'function' ? response.getTransports() : undefined;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(response.clientDataJSON),
      attestationObject: bufToB64url(response.attestationObject),
      ...(transports && transports.length ? { transports } : {}),
    },
  };
}

/** Serialize an assertion `PublicKeyCredential` to the go-webauthn JSON shape. */
export function assertionToJson(cred: PublicKeyCredential): WebAuthnJson {
  const response = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    authenticatorAttachment: cred.authenticatorAttachment ?? undefined,
    clientExtensionResults: cred.getClientExtensionResults(),
    response: {
      clientDataJSON: bufToB64url(response.clientDataJSON),
      authenticatorData: bufToB64url(response.authenticatorData),
      signature: bufToB64url(response.signature),
      userHandle: response.userHandle ? bufToB64url(response.userHandle) : null,
    },
  };
}
